/**
 * ResponsiveLayout - Conditionally renders desktop or mobile layouts
 *
 * Breakpoints:
 * - Desktop (≥992px): DesktopLayout with sidebar
 * - Tablet (768-991px): DesktopLayout without sidebar (future)
 * - Mobile (<768px): Children as-is (current bottom tabs)
 *
 * Safety: Feature flag controls desktop layout
 */

import React from 'react';
import { useResponsive } from '@/hooks/useResponsive';
import { DesktopLayout } from './DesktopLayout';
import { View, StyleSheet } from 'react-native';
import { DisclaimerFooter } from '@components/common/DisclaimerFooter';

interface ResponsiveLayoutProps {
  children: React.ReactNode;
  rightPanel?: React.ReactNode;
  enableDesktopLayout?: boolean; // Feature flag - default OFF
}

export const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({
  children,
  rightPanel,
  enableDesktopLayout = false, // Safety: Default disabled
}) => {
  const { isDesktop } = useResponsive();

  // Desktop layout only if:
  // 1. Feature flag is enabled
  // 2. AND on desktop breakpoint (≥992px)
  const shouldUseDesktopLayout = isDesktop && enableDesktopLayout;

  if (shouldUseDesktopLayout) {
    return (
      <DesktopLayout showSidebar rightPanel={rightPanel}>
        {children}
      </DesktopLayout>
    );
  }

  // Mobile/tablet - return children as-is (no layout wrapper)
  // This keeps current bottom tabs behavior unchanged
  return (
    <View style={styles.container}>
      {children}
      <DisclaimerFooter />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
